/**
 * PlotBuilderDialog — AI-powered plot extraction + structured plot editor.
 *
 * Tabs:
 *   1. Extract & Edit  — run AI extraction, edit all plot fields, save
 *   2. Synopsis Builder — AI-generated query-letter synopsis from the plot
 *
 * Structure modes: 3-Act (classic) | 5-Act (Freytag) | Dan Wells 7-Point
 *
 * "Strengthen Plot" compares the extracted beats against the chosen
 * structure's ideal shape and rewrites missing / weak turning points.
 *
 * Available to Pro / Studio tier users.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Loader2, GitBranch, RefreshCw, Save, ChevronDown, ChevronUp,
  Sparkles, Target, BookOpenText, Zap, BarChart3, AlertCircle,
  CheckCircle2, TrendingUp, FileText, Wand2, Network, Pencil, ShieldCheck,
  Gauge, X,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { plotExtractionApi, extractionApi, subplotArcApi } from '@/lib/api';
import { cn } from '@/lib/utils';
import ContinuityReviewDrawer from '@/components/panels/ContinuityReviewDrawer';

// ─────────────────────────────────────────────────────────────────────
//  Structure definitions (mirrors backend STRUCTURE_DEFS)
// ─────────────────────────────────────────────────────────────────────
const STRUCTURES = {
  '3-act': {
    label:       'Classic 3-Act',
    description: 'Blake Snyder / Save the Cat',
    beats: [
      { key: 'inciting_incident', label: 'Inciting Incident',         hint: 'The event that pulls the hero into the conflict.' },
      { key: 'first_plot_point',  label: 'First Plot Point / Lock-In', hint: 'End of Act 1 — the hero is fully committed.' },
      { key: 'midpoint',          label: 'Midpoint Shift',             hint: 'Halfway: hero pivots from reactive to proactive.' },
      { key: 'all_is_lost',       label: 'All Is Lost',                hint: 'End of Act 2 — darkest moment before the climax.' },
      { key: 'climax',            label: 'Climax',                     hint: 'Final confrontation / peak dramatic tension.' },
      { key: 'resolution',        label: 'Resolution',                 hint: 'Aftermath and new status quo.' },
    ],
  },
  '5-act': {
    label:       '5-Act Pyramid',
    description: "Freytag's Pyramid",
    beats: [
      { key: 'exposition',      label: 'Exposition',             hint: 'World and characters established.' },
      { key: 'rising_action',   label: 'Rising Action',          hint: 'Complications escalate toward the midpoint.' },
      { key: 'climax_midpoint', label: 'Climax (Midpoint)',      hint: 'Peak tension — the point of no return.' },
      { key: 'falling_action',  label: 'Falling Action',         hint: 'Consequences unfold after the climax.' },
      { key: 'denouement',      label: 'Dénouement',            hint: 'Resolution and the new normal.' },
    ],
  },
  '7-act-dan-wells': {
    label:       'Dan Wells 7-Point',
    description: 'Hook → Resolution',
    beats: [
      { key: 'hook',         label: '1. Hook',           hint: "Introduce the protagonist's want vs need." },
      { key: 'plot_turn_1',  label: '2. Plot Turn 1',    hint: 'Inciting event that kicks off the story.' },
      { key: 'pinch_1',      label: '3. Pinch 1',        hint: 'First major obstacle — forces reaction.' },
      { key: 'midpoint',     label: '4. Midpoint',       hint: 'Hero shifts from reactive to proactive.' },
      { key: 'pinch_2',      label: '5. Pinch 2',        hint: 'All-is-lost moment — everything falls apart.' },
      { key: 'plot_turn_2',  label: '6. Plot Turn 2',    hint: "Discovery of what's needed to win." },
      { key: 'resolution',   label: '7. Resolution',     hint: 'Climax, victory/defeat, and new status quo.' },
    ],
  },
  '6-element': {
    label:       '6-Element',
    description: 'Exposition → Resolution',
    beats: [
      { key: 'exposition',        label: 'Exposition',              hint: 'World, characters, and status quo established.' },
      { key: 'inciting_incident', label: 'Inciting Incident',       hint: 'The catalyst that disrupts normal life and ignites the main conflict.' },
      { key: 'rising_action',     label: 'Rising Action',           hint: 'Escalating obstacles, complications, and character development.' },
      { key: 'climax',            label: 'Climax',                  hint: 'Peak tension and intensity — the major turning point.' },
      { key: 'falling_action',    label: 'Falling Action',          hint: 'Tension eases; loose ends start to get addressed.' },
      { key: 'resolution',        label: 'Resolution / Dénouement', hint: 'Final closure — the new status quo established.' },
    ],
  },
};

// ─────────────────────────────────────────────────────────────────────
//  Sub-components
// ─────────────────────────────────────────────────────────────────────

function ScoreRing({ score, threshold = 85 }) {
  const pct  = Math.min(100, Math.max(0, score));
  const r    = 28;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  const color = pct >= threshold ? '#10b981' : pct >= 70 ? '#f59e0b' : '#ef4444';
  return (
    <div className="relative inline-flex items-center justify-center" data-testid="plot-score-ring">
      <svg width="72" height="72" style={{ transform: 'rotate(-90deg)' }}>
        <circle cx="36" cy="36" r={r} fill="none" stroke="currentColor" strokeOpacity=".1" strokeWidth="5" />
        <circle cx="36" cy="36" r={r} fill="none" stroke={color} strokeWidth="5"
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 0.6s ease' }} />
      </svg>
      <span className="absolute text-[15px] font-bold" style={{ color }}>{pct}</span>
    </div>
  );
}

function SubScoreBar({ label, score }) {
  const color = score >= 85 ? 'bg-emerald-500' : score >= 60 ? 'bg-amber-400' : 'bg-rose-500';
  return (
    <div className="space-y-0.5">
      <div className="flex items-center justify-between text-[13px]">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-semibold tabular-nums">{score}</span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div className={cn('h-full rounded-full transition-all', color)} style={{ width: `${score}%` }} />
      </div>
    </div>
  );
}

function FieldRow({ label, field, value, onChange, rows = 2, hint }) {
  return (
    <div className="space-y-1" data-testid={`plot-field-${field}`}>
      <label className="text-[13px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</label>
      {hint && <p className="text-[12px] text-muted-foreground/70">{hint}</p>}
      <Textarea value={value || ''} onChange={(e) => onChange(field, e.target.value)}
        rows={rows} className="resize-y text-[14px] leading-relaxed min-h-[56px]"
        data-testid={`plot-input-${field}`} />
    </div>
  );
}

function Section({ title, icon: Icon, open, onToggle, children }) {
  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <button type="button" onClick={onToggle}
        className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-muted/40 transition-colors"
        data-testid={`plot-section-${title.toLowerCase().replace(/\s+/g, '-')}`}>
        <span className="flex items-center gap-1.5 text-[13.5px] font-semibold">
          {Icon && <Icon className="h-3.5 w-3.5 text-primary" />}{title}
        </span>
        {open ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
      </button>
      {open && <div className="border-t border-border p-3 space-y-3">{children}</div>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Helpers
// ─────────────────────────────────────────────────────────────────────

function emptyPlot() {
  return {
    logline: '', central_conflict: '',
    protagonist: { goal: '', need: '', flaw: '', transformation: '' },
    antagonist: '',
    structure_type: '3-act',
    structure_beats: STRUCTURES['3-act'].beats.map((b) => ({ ...b, chapter: '', description: '' })),
    pacing_beats: STRUCTURES['3-act'].beats.map((b) => ({ key: b.key, label: b.label, pace: 5 })),
    chapter_beats: [],
    subplots: ['', '', '', '', ''],
    themes: ['', '', '', '', ''],
  };
}

function normalisePlot(pe, structureType) {
  const base = emptyPlot();
  const subplots = [...(pe.subplots || [])];
  while (subplots.length < 5) subplots.push('');
  const themes = [...(pe.themes || [])];
  while (themes.length < 5) themes.push('');

  const sType = structureType || pe.structure_type || '3-act';
  const structDef = STRUCTURES[sType] || STRUCTURES['3-act'];

  // Rebuild structure_beats from either stored structure_beats or legacy act_structure
  let structure_beats = pe.structure_beats || [];
  if (structure_beats.length === 0 && pe.act_structure) {
    structure_beats = structDef.beats.map((b) => {
      const src = (pe.act_structure || {})[b.key] || {};
      return { ...b, chapter: src.chapter || '', description: src.description || '' };
    });
  } else if (structure_beats.length > 0) {
    // Ensure all keys from current structDef are present (for structure switches)
    const byKey = Object.fromEntries(structure_beats.map((b) => [b.key, b]));
    structure_beats = structDef.beats.map((b) => ({
      ...b,
      chapter:     (byKey[b.key] || {}).chapter || '',
      description: (byKey[b.key] || {}).description || '',
    }));
  } else {
    structure_beats = structDef.beats.map((b) => ({ ...b, chapter: '', description: '' }));
  }

  return {
    ...base, ...pe,
    protagonist:     { ...base.protagonist,   ...(pe.protagonist   || {}) },
    subplots, themes,
    structure_type:  sType,
    structure_beats,
    pacing_beats:    _normalisePacingBeats(pe.pacing_beats, structDef),
  };
}

function _normalisePacingBeats(stored, structDef) {
  const byKey = Object.fromEntries((stored || []).map((b) => [b.key, b]));
  return (structDef?.beats || []).map((b) => ({
    key:   b.key,
    label: b.label,
    pace:  byKey[b.key]?.pace ?? 5,
  }));
}

// ─────────────────────────────────────────────────────────────────────
//  Subplot Arc Tracker — swimlane grid
// ─────────────────────────────────────────────────────────────────────

const BEAT_COLORS = {
  auto:   'bg-primary/80 text-primary-foreground',
  manual: 'bg-emerald-500/90 text-white',
  empty:  'bg-muted/40 hover:bg-muted/70',
};

function ArcTrackerTab({ pid, onOpenContinuity }) {
  const [arcData,      setArcData]      = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [toggling,     setToggling]     = useState(null);   // "subplotIdx:chapterId" key
  const [hoveredKey,   setHoveredKey]   = useState(null);
  // Note editor: {key, subplot_index, chapter_id, note, isActive, label}
  const [editingCell,  setEditingCell]  = useState(null);
  const noteRef = useRef(null);

  const load = useCallback(async () => {
    if (!pid) return;
    setLoading(true);
    try {
      const d = await subplotArcApi.get(pid);
      setArcData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load arc data.');
    } finally {
      setLoading(false);
    }
  }, [pid]);

  useEffect(() => { load(); }, [load]);

  // Focus note textarea when editor opens
  useEffect(() => {
    if (editingCell && noteRef.current) {
      setTimeout(() => noteRef.current?.focus(), 50);
    }
  }, [editingCell?.key]);

  // Toggle a beat (preserves existing note)
  const handleToggle = async (subplotIndex, chapterId, currentSource, existingNote) => {
    const key = `${subplotIndex}:${chapterId}`;
    setToggling(key);
    try {
      const isActive = Boolean(currentSource);
      await subplotArcApi.toggleBeat(pid, {
        subplot_index: subplotIndex,
        chapter_id:    chapterId,
        active:        !isActive,
        note:          existingNote || '',
      });
      await load();
    } catch {
      toast.error('Toggle failed');
    } finally {
      setToggling(null);
    }
  };

  // Save a note (and optionally activate the beat if it wasn't already)
  const handleSaveNote = async () => {
    if (!editingCell) return;
    const { subplot_index, chapter_id, note, isActive } = editingCell;
    const key = `${subplot_index}:${chapter_id}`;
    setToggling(key);
    try {
      await subplotArcApi.toggleBeat(pid, {
        subplot_index,
        chapter_id,
        active: isActive,  // keep existing state; note saves on either active or inactive
        note:   note.trim(),
      });
      await load();
    } catch {
      toast.error('Could not save note');
    } finally {
      setToggling(null);
      setEditingCell(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-14 gap-2 text-muted-foreground" data-testid="arc-loading">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-[13px]">Loading arc data…</span>
      </div>
    );
  }

  if (!arcData) return null;

  const { subplots = [], chapters = [], beats = [] } = arcData;

  if (subplots.length === 0) {
    return (
      <div className="space-y-3">
        {/* Continuity link even when no subplots */}
        {onOpenContinuity && (
          <button type="button" onClick={onOpenContinuity}
            className="flex items-center gap-1.5 text-[12px] text-primary hover:underline"
            data-testid="arc-continuity-link">
            <ShieldCheck className="h-3.5 w-3.5" />
            Open Full Novel Review (continuity &amp; pacing)
          </button>
        )}
        <div className="rounded-lg border border-dashed border-border bg-muted/20 px-4 py-10 text-center" data-testid="arc-empty">
          <Network className="mx-auto mb-2 h-6 w-6 text-muted-foreground/50" />
          <p className="text-[14px] text-muted-foreground">
            No subplots found. Add subplots in the <strong>Extract &amp; Edit</strong> tab and save, then come back here.
          </p>
        </div>
      </div>
    );
  }

  // Build lookup maps
  const beatMap = {};
  const noteMap = {};
  beats.forEach((b) => {
    const k = `${b.subplot_index}:${b.chapter_id}`;
    beatMap[k] = b.source;
    if (b.note) noteMap[k] = b.note;
  });

  return (
    <div className="space-y-3" data-testid="arc-tracker-panel">
      {/* Header row */}
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-[13.5px] font-semibold">Subplot Arc Tracker</p>
          <p className="text-[12px] text-muted-foreground mt-0.5">
            Click a filled cell to toggle · hover any cell and click <Pencil className="inline h-2.5 w-2.5 mx-0.5" /> to annotate
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {onOpenContinuity && (
            <Button size="sm" variant="outline" className="gap-1.5 text-[11.5px] h-7" onClick={onOpenContinuity} data-testid="arc-continuity-btn">
              <ShieldCheck className="h-3 w-3" /> Novel Review
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={load} disabled={loading} className="gap-1.5 h-7">
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 text-[11.5px] text-muted-foreground flex-wrap">
        <span className="flex items-center gap-1.5"><span className="inline-block h-3.5 w-5 rounded-sm bg-primary/80" /> Auto-detected</span>
        <span className="flex items-center gap-1.5"><span className="inline-block h-3.5 w-5 rounded-sm bg-emerald-500/90" /> Manual</span>
        <span className="flex items-center gap-1.5"><span className="inline-block h-3.5 w-5 rounded-sm bg-muted/40 border border-border/60" /> No beat</span>
        <span className="flex items-center gap-1.5"><span className="inline-block h-2 w-2 rounded-full bg-amber-400" /> Has note</span>
      </div>

      {/* Swimlane grid */}
      <div className="overflow-auto rounded-md border border-border" style={{ maxHeight: '48vh' }}>
        <table className="min-w-full border-collapse text-[11.5px]">
          <thead className="sticky top-0 z-10 bg-muted/80 backdrop-blur-sm">
            <tr>
              <th className="sticky left-0 z-20 bg-muted/80 min-w-[140px] max-w-[180px] px-3 py-2 text-left font-semibold text-muted-foreground border-b border-border border-r border-border/50">
                Subplot
              </th>
              {chapters.map((ch) => (
                <th
                  key={ch.id}
                  className="min-w-[44px] w-11 px-1 py-2 text-center font-semibold text-muted-foreground border-b border-border border-r border-border/30"
                  title={ch.title}
                >
                  <span className="block truncate">Ch {ch.number}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {subplots.map((sp) => (
              <tr key={sp.index} className="hover:bg-muted/10 transition-colors">
                {/* Subplot label */}
                <td className="sticky left-0 bg-background border-b border-border border-r border-border/50 px-3 py-2">
                  <span
                    className="block truncate font-medium text-[12px] leading-snug"
                    title={sp.title}
                    style={{ maxWidth: '160px' }}
                  >
                    {sp.title}
                  </span>
                  {sp.description && (
                    <span className="block truncate text-[10px] text-muted-foreground" style={{ maxWidth: '160px' }}>
                      {sp.description}
                    </span>
                  )}
                </td>
                {/* Chapter cells */}
                {chapters.map((ch) => {
                  const key        = `${sp.index}:${ch.id}`;
                  const source     = beatMap[key];
                  const cellNote   = noteMap[key] || '';
                  const isToggling = toggling === key;
                  const isHovered  = hoveredKey === key;
                  const isEditing  = editingCell?.key === key;

                  return (
                    <td
                      key={ch.id}
                      className="relative border-b border-border border-r border-border/30 p-1 text-center"
                      onMouseEnter={() => setHoveredKey(key)}
                      onMouseLeave={() => { if (!isEditing) setHoveredKey(null); }}
                    >
                      {/* Beat cell button */}
                      <button
                        type="button"
                        onClick={() => handleToggle(sp.index, ch.id, source, cellNote)}
                        disabled={isToggling}
                        title={cellNote || (source ? `Beat (${source}) — click to remove` : 'No beat — click to mark')}
                        className={cn(
                          'mx-auto flex h-7 w-7 items-center justify-center rounded-sm transition-all',
                          isToggling ? 'opacity-50' : '',
                          source ? BEAT_COLORS[source] || BEAT_COLORS.manual : BEAT_COLORS.empty,
                        )}
                        data-testid={`arc-cell-${sp.index}-${ch.number}`}
                      >
                        {isToggling && <Loader2 className="h-3 w-3 animate-spin" />}
                      </button>

                      {/* Note indicator dot */}
                      {cellNote && !isToggling && (
                        <span
                          className="absolute top-0.5 right-0.5 h-1.5 w-1.5 rounded-full bg-amber-400 pointer-events-none"
                          title={cellNote}
                        />
                      )}

                      {/* Pencil icon — visible on hover */}
                      {isHovered && !isToggling && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingCell({
                              key,
                              subplot_index: sp.index,
                              chapter_id:    ch.id,
                              note:          cellNote,
                              isActive:      Boolean(source),
                              label:         `${sp.title} · Ch ${ch.number}`,
                            });
                          }}
                          className="absolute top-0 left-0 flex h-4 w-4 items-center justify-center rounded-br-sm bg-background/90 text-muted-foreground hover:text-primary transition-colors"
                          title="Add / edit note"
                          data-testid={`arc-pencil-${sp.index}-${ch.number}`}
                        >
                          <Pencil className="h-2.5 w-2.5" />
                        </button>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Inline note editor — shown below the grid when a cell is being annotated */}
      {editingCell && (
        <div
          className="rounded-md border border-border bg-muted/20 p-3 space-y-2 animate-in fade-in slide-in-from-top-1 duration-150"
          data-testid="arc-note-editor"
        >
          <div className="flex items-center justify-between">
            <p className="text-[12.5px] font-semibold truncate">{editingCell.label}</p>
            <button
              type="button"
              onClick={() => { setEditingCell(null); setHoveredKey(null); }}
              className="text-muted-foreground hover:text-foreground ml-2 flex-shrink-0 text-[11px]"
            >
              ✕
            </button>
          </div>
          <textarea
            ref={noteRef}
            value={editingCell.note}
            onChange={(e) => setEditingCell((prev) => ({ ...prev, note: e.target.value }))}
            placeholder="What happens in this chapter for this subplot?"
            className="w-full resize-none rounded-sm border border-border bg-background px-2 py-1.5 text-[12px] focus:outline-none focus:border-primary"
            rows={3}
          />
          <div className="flex items-center justify-between">
            <p className="text-[10.5px] text-muted-foreground">Note is optional — visible on cell hover</p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="text-[11.5px] text-muted-foreground hover:text-foreground"
                onClick={() => { setEditingCell(null); setHoveredKey(null); }}
              >
                Cancel
              </button>
              <Button
                size="sm"
                className="h-6 gap-1 px-2.5 text-[11.5px]"
                onClick={handleSaveNote}
                disabled={toggling === editingCell.key}
                data-testid="arc-note-save-btn"
              >
                {toggling === editingCell.key
                  ? <Loader2 className="h-3 w-3 animate-spin" />
                  : <Save className="h-3 w-3" />}
                Save note
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Pacing helpers
// ─────────────────────────────────────────────────────────────────────

/**
 * Each preset defines a canonical 6-point curve (matching the 6-element
 * structure). For other beat counts the curve is linearly interpolated.
 * Values are on the 1–10 pace scale.
 */
const PACE_PRESETS = [
  {
    key:         'thriller',
    name:        'Thriller Rhythm',
    tagline:     'Relentless escalation to peak tension',
    color:       '#ef4444',   // red
    curve:       [4, 7, 8, 10, 6, 4],
  },
  {
    key:         'slow-burn',
    name:        'Literary Slow-Burn',
    tagline:     'Reflective, character-driven depth',
    color:       '#6366f1',   // indigo
    curve:       [2, 3, 4, 6, 3, 2],
  },
  {
    key:         'romance',
    name:        'Romance Arc',
    tagline:     'Emotional crescendo, warm resolution',
    color:       '#ec4899',   // pink
    curve:       [4, 5, 7, 9, 6, 5],
  },
  {
    key:         'mystery',
    name:        'Mystery Unraveling',
    tagline:     'Deliberate set-up, sharp reveal peaks',
    color:       '#0ea5e9',   // sky
    curve:       [3, 7, 5, 9, 7, 4],
  },
  {
    key:         'epic',
    name:        'Epic Fantasy',
    tagline:     'World-building sweep to world-shattering climax',
    color:       '#f59e0b',   // amber
    curve:       [2, 4, 6, 10, 5, 3],
  },
];

/**
 * Scale a preset's 6-point canonical curve to any N beats via
 * linear interpolation, clamping to [1, 10].
 */
function interpolatePreset(curve, n) {
  if (n === curve.length) return [...curve];
  if (n === 1) return [Math.round(curve[Math.floor(curve.length / 2)])];
  return Array.from({ length: n }, (_, i) => {
    const t  = i / (n - 1);
    const fi = t * (curve.length - 1);
    const lo = Math.floor(fi);
    const hi = Math.min(lo + 1, curve.length - 1);
    return Math.min(10, Math.max(1, Math.round(curve[lo] + (fi - lo) * (curve[hi] - curve[lo]))));
  });
}

/** Returns the key of the preset whose curve exactly matches current rows, or null. */
function detectActivePreset(rows) {
  const vals = rows.map((r) => r.pace);
  for (const p of PACE_PRESETS) {
    const scaled = interpolatePreset(p.curve, rows.length);
    if (scaled.every((v, i) => v === vals[i])) return p.key;
  }
  return null;
}

/** Tiny inline SVG curve thumbnail shown inside each preset pill. */
function PresetMiniCurve({ curve, color, n }) {
  const scaledCurve = interpolatePreset(curve, n);
  const W = 36, H = 16, PAD = 2;
  const xStep = (W - PAD * 2) / Math.max(scaledCurve.length - 1, 1);
  const yScale = (v) => PAD + ((10 - v) / 9) * (H - PAD * 2);
  const pts = scaledCurve.map((v, i) => ({ x: PAD + i * xStep, y: yScale(v) }));
  const d = pts.reduce((acc, pt, i) => {
    if (i === 0) return `M${pt.x},${pt.y}`;
    const prev = pts[i - 1];
    const cx   = (prev.x + pt.x) / 2;
    return `${acc} C${cx},${prev.y} ${cx},${pt.y} ${pt.x},${pt.y}`;
  }, '');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} aria-hidden="true">
      <path d={d} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

const PACE_LEVELS = [
  { min: 1,  max: 2,  label: 'Deliberate', color: '#6366f1' },   // indigo
  { min: 3,  max: 4,  label: 'Measured',   color: '#0ea5e9' },   // sky
  { min: 5,  max: 6,  label: 'Moderate',   color: '#10b981' },   // emerald
  { min: 7,  max: 8,  label: 'Brisk',      color: '#f59e0b' },   // amber
  { min: 9,  max: 10, label: 'Breakneck',  color: '#ef4444' },   // red
];

function paceLevel(v) {
  return PACE_LEVELS.find((l) => v >= l.min && v <= l.max) || PACE_LEVELS[2];
}

/** SVG sparkline drawn across all pace values. */
function PaceSparkline({ beats }) {
  const W = 560, H = 72, PAD = 12;
  const n = beats.length;
  if (n === 0) return null;

  const xStep = (W - PAD * 2) / Math.max(n - 1, 1);
  const yScale = (v) => PAD + ((10 - v) / 9) * (H - PAD * 2);

  const points = beats.map((b, i) => ({
    x: PAD + i * xStep,
    y: yScale(b.pace ?? 5),
    pace: b.pace ?? 5,
    label: b.label,
  }));

  // Smooth cubic bezier path
  const pathD = points.reduce((acc, pt, i) => {
    if (i === 0) return `M${pt.x},${pt.y}`;
    const prev = points[i - 1];
    const cx = (prev.x + pt.x) / 2;
    return `${acc} C${cx},${prev.y} ${cx},${pt.y} ${pt.x},${pt.y}`;
  }, '');

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="w-full"
      style={{ height: H }}
      aria-hidden="true"
      data-testid="pacing-sparkline"
    >
      {/* Horizontal guide lines */}
      {[2, 5, 8].map((v) => (
        <line
          key={v}
          x1={PAD} y1={yScale(v)} x2={W - PAD} y2={yScale(v)}
          stroke="currentColor" strokeOpacity=".08" strokeWidth="1"
        />
      ))}

      {/* Filled area under curve */}
      <path
        d={`${pathD} L${points[n - 1].x},${H} L${points[0].x},${H} Z`}
        fill="url(#pace-grad)"
        opacity=".18"
      />

      {/* Gradient definition */}
      <defs>
        <linearGradient id="pace-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#f59e0b" />
          <stop offset="100%" stopColor="#6366f1" />
        </linearGradient>
      </defs>

      {/* Curve */}
      <path d={pathD} fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" />

      {/* Dots + labels */}
      {points.map((pt, i) => {
        const lvl = paceLevel(pt.pace);
        return (
          <g key={beats[i].key || i}>
            <circle cx={pt.x} cy={pt.y} r="4" fill={lvl.color} />
            <text
              x={pt.x}
              y={H - 2}
              textAnchor="middle"
              fontSize="8"
              fill="currentColor"
              opacity=".55"
            >
              {beats[i].label.split(' ')[0]}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/** One slider row per plot element. */
function PacingTab({ plot, structDef, onPacingChange, onApplyPreset, onSave, saving }) {
  const pacingBeats = plot?.pacing_beats || [];

  // Build display list merging structDef order with stored pace values
  const byKey = Object.fromEntries(pacingBeats.map((b) => [b.key, b]));
  const rows = (structDef?.beats || []).map((b) => ({
    ...b,
    pace: byKey[b.key]?.pace ?? 5,
  }));

  const hasData = Boolean(plot?.logline || plot?.central_conflict || (plot?.structure_beats || []).some((b) => b.description));
  const activePresetKey = hasData ? detectActivePreset(rows) : null;
  const n = rows.length;

  return (
    <div className="space-y-4" data-testid="pacing-tab">
      {/* Intro */}
      <div className="rounded-lg border border-border/60 bg-muted/20 px-3 py-2.5 space-y-0.5">
        <p className="text-[12px] font-semibold uppercase tracking-wider text-muted-foreground">Pacing per Element</p>
        <p className="text-[12px] text-muted-foreground/70">
          Set the intended narrative pace for each plot element. This is fed to the AI as structural guidance.
        </p>
      </div>

      {/* ── Preset strip (always visible so authors can start from a template) */}
      <div className="space-y-1.5">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Presets
        </p>
        <div
          className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1"
          style={{ scrollbarWidth: 'none' }}
          data-testid="preset-strip"
        >
          {PACE_PRESETS.map((preset) => {
            const isActive = activePresetKey === preset.key;
            return (
              <button
                key={preset.key}
                type="button"
                onClick={() => onApplyPreset(preset)}
                data-testid={`preset-btn-${preset.key}`}
                className="flex-shrink-0 flex flex-col gap-1 rounded-lg border px-3 py-2 text-left transition-all duration-150 focus:outline-none"
                style={{
                  borderColor:     isActive ? preset.color : undefined,
                  backgroundColor: isActive ? `${preset.color}18` : undefined,
                  minWidth: 140,
                }}
              >
                <div className="flex items-center justify-between gap-2">
                  <span
                    className="text-[12px] font-semibold leading-tight"
                    style={{ color: isActive ? preset.color : undefined }}
                  >
                    {preset.name}
                  </span>
                  <PresetMiniCurve curve={preset.curve} color={preset.color} n={n} />
                </div>
                <span className="text-[10.5px] text-muted-foreground/70 leading-tight">{preset.tagline}</span>
                {isActive && (
                  <span
                    className="self-start rounded-full px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wider text-white"
                    style={{ background: preset.color }}
                    data-testid={`preset-active-badge-${preset.key}`}
                  >
                    Active
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {!hasData && (
        <div className="rounded-lg border border-dashed border-border bg-muted/20 px-4 py-8 text-center">
          <Gauge className="mx-auto mb-2 h-6 w-6 text-muted-foreground/50" />
          <p className="text-[14px] text-muted-foreground">
            No plot data yet. Run <strong>Extract from manuscript</strong> or fill in the Extract &amp; Edit tab first.
          </p>
        </div>
      )}

      {hasData && (
        <>
          {/* Sparkline */}
          <div className="rounded-lg border border-border/60 overflow-hidden px-2 pt-2 pb-1 bg-muted/10">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1 px-1">Pace Curve</p>
            <PaceSparkline beats={rows} />
            {/* Legend */}
            <div className="flex flex-wrap items-center gap-3 px-1 pb-1 mt-1">
              {PACE_LEVELS.map((l) => (
                <span key={l.label} className="flex items-center gap-1 text-[10.5px] text-muted-foreground">
                  <span className="inline-block h-2 w-2 rounded-full" style={{ background: l.color }} />
                  {l.min}–{l.max} {l.label}
                </span>
              ))}
            </div>
          </div>

          {/* Slider rows */}
          <div className="space-y-3">
            {rows.map((row, idx) => {
              const lvl = paceLevel(row.pace);
              return (
                <div
                  key={row.key}
                  className="rounded-lg border border-border/70 px-3 py-2.5 space-y-1.5"
                  data-testid={`pacing-row-${row.key}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <span className="text-[13px] font-semibold">{row.label}</span>
                      {row.hint && (
                        <span className="ml-2 text-[11.5px] text-muted-foreground/70">{row.hint}</span>
                      )}
                    </div>
                    <span
                      className="flex-shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold text-white"
                      style={{ background: lvl.color }}
                      data-testid={`pacing-label-${row.key}`}
                    >
                      {lvl.label}
                    </span>
                  </div>

                  <div className="flex items-center gap-3">
                    <span className="w-16 text-[10.5px] text-muted-foreground text-right shrink-0">Slow</span>
                    <input
                      type="range"
                      min="1"
                      max="10"
                      step="1"
                      value={row.pace}
                      onChange={(e) => onPacingChange(idx, parseInt(e.target.value, 10))}
                      className="flex-1 h-1.5 accent-amber-500 cursor-pointer"
                      data-testid={`pacing-slider-${row.key}`}
                      style={{ accentColor: lvl.color }}
                    />
                    <span className="w-16 text-[10.5px] text-muted-foreground shrink-0">Breakneck</span>
                    <span
                      className="w-6 text-center font-mono text-[13px] font-bold shrink-0"
                      style={{ color: lvl.color }}
                      data-testid={`pacing-value-${row.key}`}
                    >
                      {row.pace}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Save */}
          <div className="flex justify-end pt-1">
            <Button
              size="sm"
              onClick={onSave}
              disabled={saving}
              data-testid="pacing-save-btn"
              className="gap-1.5"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              Save pacing
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Main panel
// ─────────────────────────────────────────────────────────────────────

export function PlotBuilderPanel({ project, inDialog = false, onClose, open = true }) {
  const pid = project?.id;

  const [activeTab,      setActiveTab]      = useState('extract');   // 'extract' | 'synopsis' | 'arc'
  const [plot,           setPlot]           = useState(null);
  const [dirty,          setDirty]          = useState(false);
  const [loading,        setLoading]        = useState(false);
  const [extracting,     setExtracting]     = useState(false);
  const [strengthening,  setStrengthening]  = useState(false);
  const [buildingSyn,    setBuildingSyn]    = useState(false);
  const [saving,         setSaving]         = useState(false);
  const [scoreData,      setScoreData]      = useState(null);
  const [scoreLoading,   setScoreLoading]   = useState(false);
  const [synopsis,       setSynopsis]       = useState('');
  const [error,          setError]          = useState(null);
  const [suggestingSubplotIdx, setSuggestingSubplotIdx] = useState(null);
  // Extraction draft — used to enrich subplot suggestions with real cast + world data
  const [extractionDraft, setExtractionDraft] = useState(null);
  // Continuity Review Drawer
  const [showContinuityDrawer, setShowContinuityDrawer] = useState(false);

  const [openSections, setOpenSections] = useState({
    core: true, protagonist: true, actStructure: true,
    chapters: false, subplots: false, score: true,
  });
  const toggleSection = (k) => setOpenSections((p) => ({ ...p, [k]: !p[k] }));

  // Derive selected structure type
  const structureType = plot?.structure_type || '3-act';
  const structDef     = STRUCTURES[structureType] || STRUCTURES['3-act'];

  // Load cached extraction on mount
  useEffect(() => {
    if (!pid) return;
    setLoading(true);
    plotExtractionApi.get(pid)
      .then((d) => {
        const pe = d.plot_extraction || {};
        setPlot(normalisePlot(pe, pe.structure_type));
        setSynopsis(pe.generated_synopsis || '');
        setDirty(false);
      })
      .catch((e) => {
        if (e?.response?.status !== 404) {
          setError(e?.response?.data?.detail || 'Could not load plot data.');
        }
        setPlot(emptyPlot());
      })
      .finally(() => setLoading(false));

    // Silently load extraction draft for subplot enrichment (errors are non-fatal)
    extractionApi.getDraft(pid)
      .then((d) => { if (d?.status === 'done') setExtractionDraft(d); })
      .catch(() => {});
  }, [pid]);

  // ── Field handlers ────────────────────────────────────────────────
  const handleField = useCallback((field, value) => {
    setPlot((p) => ({ ...p, [field]: value }));
    setDirty(true);
  }, []);

  const handleProtField = useCallback((field, value) => {
    setPlot((p) => ({ ...p, protagonist: { ...(p.protagonist || {}), [field]: value } }));
    setDirty(true);
  }, []);

  const handleBeatField = useCallback((idx, subfield, value) => {
    setPlot((p) => {
      const beats = [...(p.structure_beats || [])];
      beats[idx] = { ...(beats[idx] || {}), [subfield]: value };
      return { ...p, structure_beats: beats };
    });
    setDirty(true);
  }, []);

  const handleChapterBeatField = useCallback((idx, subfield, value) => {
    setPlot((p) => {
      const beats = [...(p.chapter_beats || [])];
      beats[idx] = { ...(beats[idx] || {}), [subfield]: value };
      return { ...p, chapter_beats: beats };
    });
    setDirty(true);
  }, []);

  const handleListField = useCallback((field, idx, value) => {
    setPlot((p) => {
      const arr = [...(p[field] || [])];
      arr[idx] = value;
      return { ...p, [field]: arr };
    });
    setDirty(true);
  }, []);

  const handlePacingChange = useCallback((idx, pace) => {
    setPlot((p) => {
      const beats = [...(p.pacing_beats || [])];
      beats[idx] = { ...(beats[idx] || {}), pace };
      return { ...p, pacing_beats: beats };
    });
    setDirty(true);
  }, []);

  const handleApplyPreset = useCallback((preset) => {
    setPlot((p) => {
      const structBeats = STRUCTURES[p.structure_type]?.beats || [];
      const scaled = interpolatePreset(preset.curve, structBeats.length);
      const pacing_beats = structBeats.map((b, i) => ({
        key:   b.key,
        label: b.label,
        pace:  scaled[i] ?? 5,
      }));
      return { ...p, pacing_beats };
    });
    setDirty(true);
    toast.success(`"${preset.name}" preset applied — hit Save pacing to keep it.`);
  }, []);

  // ── Suggest subplot ───────────────────────────────────────────────
  const handleSuggestSubplot = useCallback(async (idx) => {
    if (!pid) return;
    setSuggestingSubplotIdx(idx);
    try {
      // Base context from the current plot
      const context = {
        index:            idx,
        logline:          plot?.logline,
        central_conflict: plot?.central_conflict,
        protagonist:      plot?.protagonist,
        antagonist:       plot?.antagonist,
        subplots:         plot?.subplots,
        themes:           plot?.themes,
      };
      // Enrich with extraction draft — real cast and world entries ground the suggestion
      if (extractionDraft) {
        context.characters = (extractionDraft.characters || []).map((c) => ({
          name:        c.name,
          role:        c.role,
          arc_summary: c.integration?.arc_summary || c.arc || '',
        }));
        context.codex_entries = (extractionDraft.codex_entries || []).map((e) => ({
          name:       e.name,
          entry_type: e.entry_type,
          summary:    e.summary,
        }));
      }
      const res = await plotExtractionApi.suggestSubplot(pid, idx, context);
      if (res?.subplot) {
        handleListField('subplots', idx, res.subplot);
        toast.success(`Subplot ${idx + 1} suggested.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Suggestion failed.');
    } finally {
      setSuggestingSubplotIdx(null);
    }
  }, [pid, plot, extractionDraft, handleListField]);

  // ── Structure change ────────────────────────────────────────────────
  const handleStructureChange = (sType) => {
    setPlot((p) => normalisePlot({ ...(p || {}), structure_type: sType }, sType));
    setDirty(true);
  };

  // ── Extract ────────────────────────────────────────────────────────
  const runExtraction = async () => {
    if (!pid) return;
    setExtracting(true);
    setError(null);
    try {
      const data = await plotExtractionApi.extract(pid, structureType);
      setPlot(normalisePlot(data.plot_extraction || {}, structureType));
      setDirty(false);
      setScoreData(null);
      toast.success('Plot extracted from manuscript.', {
        description: 'Open Arc Visualizer → Story Map to see characters mapped to your acts.',
        action: {
          label: 'Story Map →',
          onClick: () => {
            window.dispatchEvent(new CustomEvent('loom:open-panel', { detail: 'arc' }));
            setTimeout(() => window.dispatchEvent(new CustomEvent('loom:arc-switch-tab', { detail: 'storymap' })), 150);
          },
        },
      });
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Extraction failed.';
      setError(msg.includes('Pro') || msg.includes('Studio')
        ? 'Plot Builder requires a Pro or Studio plan.'
        : msg);
      toast.error(msg);
    } finally {
      setExtracting(false);
    }
  };

  // ── Strengthen ─────────────────────────────────────────────────────
  const runStrengthen = async () => {
    if (!pid) return;
    setStrengthening(true);
    setError(null);
    try {
      const res = await plotExtractionApi.strengthen(pid, structureType);
      setPlot(normalisePlot(res.plot_extraction || {}, structureType));
      setDirty(false);
      if (res.strengthen_notes) {
        toast.success(`Plot strengthened: ${res.strengthen_notes}`);
      } else {
        toast.success('Plot strengthened successfully.');
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Strengthen failed.';
      setError(msg);
      toast.error(msg);
    } finally {
      setStrengthening(false);
    }
  };

  // ── Save ───────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!pid || !plot) return;
    setSaving(true);
    try {
      const patch = {
        logline:          plot.logline,
        central_conflict: plot.central_conflict,
        protagonist:      plot.protagonist,
        antagonist:       plot.antagonist,
        structure_type:   plot.structure_type,
        structure_beats:  plot.structure_beats,
        pacing_beats:     plot.pacing_beats,
        chapter_beats:    plot.chapter_beats,
        subplots:         (plot.subplots || []).filter(Boolean),
        themes:           (plot.themes   || []).filter(Boolean),
      };
      const res = await plotExtractionApi.update(pid, patch);
      setPlot(normalisePlot(res.plot_extraction || {}, structureType));
      setDirty(false);
      toast.success('Plot saved.');
      window.dispatchEvent(new CustomEvent('loom:plot-saved'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  // ── Build synopsis ─────────────────────────────────────────────────
  const handleBuildSynopsis = async () => {
    if (!pid) return;
    setBuildingSyn(true);
    setSynopsis('');
    try {
      const res = await plotExtractionApi.buildSynopsis(pid, structureType);
      setSynopsis(res.synopsis || '');
      toast.success('Synopsis built.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Synopsis generation failed.');
    } finally {
      setBuildingSyn(false);
    }
  };

  // ── Score ──────────────────────────────────────────────────────────
  const loadScore = async () => {
    if (!pid) return;
    setScoreLoading(true);
    try {
      const data = await plotExtractionApi.salesReadiness(pid);
      setScoreData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not compute score.');
    } finally {
      setScoreLoading(false);
    }
  };

  if (loading) {
    const loadingBody = (
      <div className="flex flex-col items-center gap-3 py-14 text-center text-muted-foreground" data-testid="plot-builder-loading">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
        <p className="text-[14px]">Loading plot data…</p>
      </div>
    );
    if (!inDialog) return <div className="p-4">{loadingBody}</div>;
    return (
      <Dialog open={open} onOpenChange={(v) => { if (!v && onClose) onClose(); }}>
        <DialogContent
          className="flex flex-col overflow-hidden p-0"
          style={{ width: '75vw', height: '75vh', maxWidth: '75vw', maxHeight: '90vh', minWidth: '560px', minHeight: '480px' }}
          data-testid="plot-builder-dialog"
        >
          <div className="flex-shrink-0 flex items-start justify-between gap-2 px-6 pt-5 pb-3 border-b border-border">
            <div>
              <DialogTitle className="flex items-center gap-2 text-[15px]">
                <GitBranch className="h-4 w-4 text-primary" /> Plot Builder
              </DialogTitle>
              <DialogDescription className="mt-0.5 text-[12px]">
                Extract and strengthen your manuscript's plot structure.
              </DialogDescription>
            </div>
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                data-testid="plot-builder-close-btn"
                className="mt-0.5 flex-shrink-0 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                aria-label="Close Plot Builder"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="flex-1 overflow-y-auto px-6 py-3">{loadingBody}</div>
        </DialogContent>
      </Dialog>
    );
  }

  const hasExtraction = Boolean(plot?.logline || plot?.central_conflict);
  const extractedAt   = plot?.extracted_at ? new Date(plot.extracted_at).toLocaleDateString() : null;
  const storedScore   = plot?.sales_readiness_score;

  // ── Structure selector ────────────────────────────────────────────
  const structureSelector = (
    <div className="flex flex-wrap gap-1.5">
      {Object.entries(STRUCTURES).map(([key, def]) => (
        <button
          key={key}
          type="button"
          onClick={() => handleStructureChange(key)}
          className={cn(
            'rounded-full border px-3 py-1 text-[12px] font-medium transition-all',
            structureType === key
              ? 'border-primary bg-primary/10 text-primary'
              : 'border-border text-muted-foreground hover:border-primary/40',
          )}
          data-testid={`structure-btn-${key}`}
        >
          {def.label}
          <span className="ml-1 text-[10.5px] text-muted-foreground hidden sm:inline">— {def.description}</span>
        </button>
      ))}
    </div>
  );

  // ── Extract & Edit tab body ────────────────────────────────────────
  const extractTab = (
    <div className="space-y-3">
      {/* Structure picker */}
      <div className="rounded-lg border border-border/60 bg-muted/20 px-3 py-2.5 space-y-1.5">
        <p className="text-[12px] font-semibold uppercase tracking-wider text-muted-foreground">Narrative Structure</p>
        {structureSelector}
      </div>

      {/* Action bar */}
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={runExtraction} disabled={extracting || strengthening || saving}
          data-testid="plot-extract-btn" className="gap-1.5">
          {extracting
            ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Extracting…</>
            : <><Sparkles className="h-3.5 w-3.5" /> {hasExtraction ? 'Re-extract from manuscript' : 'Extract from manuscript'}</>}
        </Button>

        {hasExtraction && (
          <Button size="sm" variant="outline" onClick={runStrengthen}
            disabled={strengthening || extracting || saving}
            data-testid="plot-strengthen-btn" className="gap-1.5">
            {strengthening
              ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Strengthening…</>
              : <><Wand2 className="h-3.5 w-3.5" /> Strengthen Plot</>}
          </Button>
        )}

        {dirty && (
          <Button size="sm" variant="outline" onClick={handleSave} disabled={saving || extracting}
            data-testid="plot-save-btn" className="gap-1.5">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Save changes
          </Button>
        )}

        {extractedAt && (
          <span className="ml-auto text-[12px] text-muted-foreground">
            Last extracted {extractedAt}
            {plot?.strengthened_at && <span className="ml-1">· strengthened {new Date(plot.strengthened_at).toLocaleDateString()}</span>}
          </span>
        )}
      </div>

      {plot?.strengthen_notes && (
        <div className="flex items-start gap-2 rounded-md bg-emerald-500/8 px-3 py-2 text-[13px] text-emerald-700">
          <Wand2 className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
          <span><strong>Strengthen notes:</strong> {plot.strengthen_notes}</span>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 rounded-md bg-destructive/10 px-3 py-2.5 text-[13.5px] text-destructive" data-testid="plot-error">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {!hasExtraction && !extracting && (
        <div className="rounded-lg border border-dashed border-border bg-muted/20 px-4 py-8 text-center">
          <GitBranch className="mx-auto mb-2 h-6 w-6 text-muted-foreground/50" />
          <p className="text-[14px] text-muted-foreground">
            No plot data yet. Pick a structure above, then click{' '}
            <strong>Extract from manuscript</strong> to analyse your chapters.
          </p>
        </div>
      )}

      {/* Core story */}
      <Section title="Core Story" icon={Target} open={openSections.core} onToggle={() => toggleSection('core')}>
        <FieldRow label="Logline / Premise" field="logline" value={plot?.logline} onChange={handleField} rows={2}
          hint="1–2 sentences capturing the central dramatic question." />
        <FieldRow label="Central Conflict" field="central_conflict" value={plot?.central_conflict} onChange={handleField} rows={2} />
        <FieldRow label="Antagonist / Opposition Force" field="antagonist" value={plot?.antagonist} onChange={handleField} rows={1} />
      </Section>

      {/* Protagonist arc */}
      <Section title="Protagonist Arc" icon={TrendingUp} open={openSections.protagonist} onToggle={() => toggleSection('protagonist')}>
        {[
          { field: 'goal',           label: 'Goal (external want)',           rows: 1 },
          { field: 'need',           label: 'Need (internal / thematic)',     rows: 1 },
          { field: 'flaw',           label: 'Flaw / wound holding them back', rows: 1 },
          { field: 'transformation', label: 'Transformation by the end',      rows: 2 },
        ].map(({ field, label, rows }) => (
          <div key={field} className="space-y-1" data-testid={`plot-prot-${field}`}>
            <label className="text-[13px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</label>
            <Textarea value={plot?.protagonist?.[field] || ''}
              onChange={(e) => handleProtField(field, e.target.value)}
              rows={rows} className="resize-y text-[14px] leading-relaxed min-h-[56px]"
              data-testid={`plot-input-prot-${field}`} />
          </div>
        ))}
      </Section>

      {/* Act structure — dynamic per chosen structure */}
      <Section
        title={`${structDef.label} — Turning Points`}
        icon={BookOpenText}
        open={openSections.actStructure}
        onToggle={() => toggleSection('actStructure')}
      >
        <div className="space-y-3">
          {(plot?.structure_beats || []).map((beat, idx) => {
            const filled = Boolean(beat.description);
            return (
              <div key={beat.key || idx} className="space-y-1.5" data-testid={`plot-beat-${beat.key}`}>
                <div className="flex items-start gap-1.5">
                  {filled
                    ? <CheckCircle2 className="mt-0.5 h-3 w-3 text-emerald-500 flex-shrink-0" />
                    : <div className="mt-0.5 h-3 w-3 flex-shrink-0 rounded-full border border-muted-foreground/40" />}
                  <div>
                    <label className="text-[13px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {beat.label}
                    </label>
                    {beat.hint && <p className="text-[11.5px] text-muted-foreground/70">{beat.hint}</p>}
                  </div>
                </div>
                <div className="grid grid-cols-[3rem_1fr] gap-2 pl-4">
                  <div>
                    <label className="text-[11.5px] text-muted-foreground">Ch.</label>
                    <input type="number"
                      className="w-full rounded-md border border-border bg-background px-2 py-1 text-[13px] text-center"
                      value={beat.chapter || ''}
                      onChange={(e) => handleBeatField(idx, 'chapter', e.target.value ? parseInt(e.target.value, 10) : '')}
                      data-testid={`plot-beat-${beat.key}-chapter`} />
                  </div>
                  <Textarea value={beat.description || ''}
                    onChange={(e) => handleBeatField(idx, 'description', e.target.value)}
                    rows={2} className="resize-y text-[13.5px] leading-relaxed min-h-[52px]"
                    placeholder="What happens at this turning point?"
                    data-testid={`plot-beat-${beat.key}-description`} />
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      {/* Chapter beats */}
      <Section title={`Chapter Beats (${(plot?.chapter_beats || []).length})`} icon={Zap}
        open={openSections.chapters} onToggle={() => toggleSection('chapters')}>
        {(plot?.chapter_beats || []).length === 0 ? (
          <p className="text-[13.5px] text-muted-foreground">No chapter beats yet. Run extraction to populate.</p>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
            {(plot.chapter_beats || []).map((beat, idx) => (
              <div key={idx} className="grid grid-cols-[2.5rem_5rem_3rem_1fr] gap-2 items-start" data-testid={`plot-chbeat-${idx}`}>
                <span className="pt-1.5 text-center font-mono text-[13px] text-muted-foreground">
                  {beat.chapter_number ?? idx + 1}
                </span>
                <select className="rounded-md border border-border bg-background px-1 py-1 text-[13px]"
                  value={beat.beat_type || 'transition'}
                  onChange={(e) => handleChapterBeatField(idx, 'beat_type', e.target.value)}
                  data-testid={`plot-chbeat-type-${idx}`}>
                  {['setup', 'complication', 'revelation', 'climax', 'resolution', 'transition'].map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                <div className="space-y-0.5">
                  <label className="text-[9.5px] text-muted-foreground">Hook</label>
                  <input type="number" min="0" max="100"
                    className="w-full rounded-md border border-border bg-background px-1 py-1 text-[13px] text-center"
                    value={beat.hook_strength ?? 50}
                    onChange={(e) => handleChapterBeatField(idx, 'hook_strength', parseInt(e.target.value, 10))}
                    data-testid={`plot-chbeat-hook-${idx}`} />
                </div>
                <Textarea value={beat.summary || ''}
                  onChange={(e) => handleChapterBeatField(idx, 'summary', e.target.value)}
                  rows={2} className="resize-y text-[13.5px] leading-snug min-h-[40px]"
                  data-testid={`plot-chbeat-summary-${idx}`} />
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Subplots & Themes */}
      <Section title="Subplots & Themes" icon={BookOpenText}
        open={openSections.subplots} onToggle={() => toggleSection('subplots')}>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-[13px] font-semibold uppercase tracking-wider text-muted-foreground">Subplots</label>
            {(plot?.subplots || ['', '', '', '', '']).map((val, idx) => (
              <div key={idx} className="flex items-start gap-1.5">
                <textarea
                  rows={2}
                  className="flex-1 rounded-md border border-border bg-background px-2 py-1.5 text-[13.5px] resize-y min-h-[56px] leading-snug"
                  placeholder={`Subplot ${idx + 1}`} value={val || ''}
                  onChange={(e) => handleListField('subplots', idx, e.target.value)}
                  data-testid={`plot-subplot-${idx}`} />
                <button
                  type="button"
                  title="AI suggest this subplot"
                  onClick={() => handleSuggestSubplot(idx)}
                  disabled={suggestingSubplotIdx !== null}
                  className="flex-shrink-0 flex items-center justify-center h-7 w-7 mt-1 rounded-md border border-border bg-background text-muted-foreground hover:text-primary hover:border-primary/50 transition-colors disabled:opacity-40"
                  data-testid={`plot-subplot-suggest-${idx}`}
                >
                  {suggestingSubplotIdx === idx
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : <Sparkles className="h-3.5 w-3.5" />}
                </button>
              </div>
            ))}
          </div>
          <div className="space-y-2">
            <label className="text-[13px] font-semibold uppercase tracking-wider text-muted-foreground">Core Themes</label>
            {(plot?.themes || ['', '', '', '', '']).map((val, idx) => (
              <input key={idx} type="text"
                className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-[13.5px]"
                placeholder={`Theme ${idx + 1}`} value={val || ''}
                onChange={(e) => handleListField('themes', idx, e.target.value)}
                data-testid={`plot-theme-${idx}`} />
            ))}
          </div>
        </div>
      </Section>

      {/* Sales Readiness Score */}
      <Section title="Sales Readiness Score" icon={BarChart3}
        open={openSections.score} onToggle={() => toggleSection('score')}>
        <div className="flex flex-col items-center gap-3">
          {scoreData ? (
            <>
              <div className="flex items-center gap-4">
                <ScoreRing score={scoreData.score} threshold={scoreData.threshold} />
                <div>
                  <p className="text-[13px] font-semibold">
                    {scoreData.market_ready
                      ? <span className="text-emerald-600">Market ready ≥ 85%</span>
                      : <span className="text-amber-600">Below 85% threshold</span>}
                  </p>
                  <p className="text-[13px] text-muted-foreground mt-0.5">
                    {scoreData.pacing_issues} pacing · {scoreData.drift_issues} drift issues
                  </p>
                </div>
              </div>
              <div className="w-full space-y-1.5">
                <SubScoreBar label="Plot structure"    score={scoreData.sub_scores?.plot_structure ?? 0} />
                <SubScoreBar label="Chapter hooks avg" score={scoreData.sub_scores?.chapter_hooks ?? 0} />
                <SubScoreBar label="Character arc"     score={scoreData.sub_scores?.character_arc ?? 0} />
                <SubScoreBar label="Pacing"            score={scoreData.sub_scores?.pacing ?? 0} />
                <SubScoreBar label="Continuity"        score={scoreData.sub_scores?.continuity ?? 0} />
              </div>
              <Button size="sm" variant="outline" onClick={loadScore} disabled={scoreLoading} className="gap-1.5" data-testid="plot-rescore-btn">
                <RefreshCw className="h-3.5 w-3.5" /> Refresh score
              </Button>
            </>
          ) : (
            <div className="flex flex-col items-center gap-2 py-2">
              {storedScore != null && (
                <div className="mb-1 flex items-center gap-3">
                  <ScoreRing score={storedScore} />
                  <p className="text-[13.5px] text-muted-foreground">Last computed: {storedScore}/100</p>
                </div>
              )}
              <Button size="sm" variant="outline" onClick={loadScore} disabled={scoreLoading}
                data-testid="plot-score-btn" className="gap-1.5">
                {scoreLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <BarChart3 className="h-3.5 w-3.5" />}
                {scoreLoading ? 'Computing…' : 'Compute Sales Readiness Score'}
              </Button>
              <p className="text-[13px] text-muted-foreground text-center max-w-xs">
                Analyses plot structure, chapter hook strength, character arc, pacing and continuity.
              </p>
            </div>
          )}
        </div>
      </Section>
    </div>
  );

  // ── Synopsis Builder tab body ────────────────────────────────────
  const synopsisTab = (
    <div className="space-y-4">
      <div className="rounded-lg border border-border/60 bg-muted/20 px-3 py-2.5 space-y-1.5">
        <p className="text-[12px] font-semibold uppercase tracking-wider text-muted-foreground">Structure</p>
        {structureSelector}
      </div>

      <div className="flex items-center gap-2">
        <Button size="sm" onClick={handleBuildSynopsis}
          disabled={buildingSyn || !hasExtraction}
          data-testid="plot-build-synopsis-btn" className="gap-1.5">
          {buildingSyn
            ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Building synopsis…</>
            : <><FileText className="h-3.5 w-3.5" /> Build synopsis from plot</>}
        </Button>
        {!hasExtraction && (
          <p className="text-[12.5px] text-muted-foreground">
            Run plot extraction first to generate a synopsis.
          </p>
        )}
      </div>

      {synopsis ? (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-[12px] font-semibold uppercase tracking-wider text-muted-foreground">
              Generated Synopsis — {STRUCTURES[structureType]?.label}
            </p>
            <Button size="sm" variant="ghost" className="gap-1 h-7 text-[12px]"
              onClick={() => {
                navigator.clipboard?.writeText(synopsis);
                toast.success('Copied to clipboard.');
              }}>
              Copy
            </Button>
          </div>
          <Textarea
            value={synopsis}
            onChange={(e) => setSynopsis(e.target.value)}
            rows={14}
            className="text-[14px] leading-relaxed resize-y min-h-[180px]"
            data-testid="plot-synopsis-text"
          />
          <p className="text-[11.5px] text-muted-foreground">
            {synopsis.split(/\s+/).filter(Boolean).length} words · Editable — changes here do not save automatically.
          </p>
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-border bg-muted/20 px-4 py-10 text-center">
          <FileText className="mx-auto mb-2 h-6 w-6 text-muted-foreground/50" />
          <p className="text-[14px] text-muted-foreground">
            No synopsis yet. {hasExtraction
              ? 'Click "Build synopsis from plot" above.'
              : 'Extract the plot first, then come back here.'}
          </p>
        </div>
      )}
    </div>
  );

  // ── Full panel ─────────────────────────────────────────────────────
  const body = (
    <div className="space-y-3">
      {/* Tab bar */}
      <div className="flex gap-1 rounded-lg bg-muted/40 p-1 w-fit">
        <button type="button" onClick={() => setActiveTab('extract')}
          className={cn('px-3 py-1.5 text-[12px] font-medium rounded-md transition-colors',
            activeTab === 'extract'
              ? 'bg-background border border-border shadow-sm text-foreground'
              : 'text-muted-foreground hover:text-foreground')}
          data-testid="plot-tab-extract">
          <GitBranch className="inline h-3 w-3 mr-1" />Extract & Edit
        </button>
        <button type="button" onClick={() => setActiveTab('pacing')}
          className={cn('px-3 py-1.5 text-[12px] font-medium rounded-md transition-colors',
            activeTab === 'pacing'
              ? 'bg-background border border-border shadow-sm text-foreground'
              : 'text-muted-foreground hover:text-foreground')}
          data-testid="plot-tab-pacing">
          <Gauge className="inline h-3 w-3 mr-1" />Pacing
        </button>
        <button type="button" onClick={() => setActiveTab('synopsis')}
          className={cn('px-3 py-1.5 text-[12px] font-medium rounded-md transition-colors',
            activeTab === 'synopsis'
              ? 'bg-background border border-border shadow-sm text-foreground'
              : 'text-muted-foreground hover:text-foreground')}
          data-testid="plot-tab-synopsis">
          <FileText className="inline h-3 w-3 mr-1" />Synopsis Builder
        </button>
        <button type="button" onClick={() => setActiveTab('arc')}
          className={cn('px-3 py-1.5 text-[12px] font-medium rounded-md transition-colors',
            activeTab === 'arc'
              ? 'bg-background border border-border shadow-sm text-foreground'
              : 'text-muted-foreground hover:text-foreground')}
          data-testid="plot-tab-arc">
          <Network className="inline h-3 w-3 mr-1" />Arc Tracker
        </button>
      </div>

      {activeTab === 'extract'  ? extractTab
        : activeTab === 'pacing'   ? (
          <PacingTab
            plot={plot}
            structDef={structDef}
            onPacingChange={handlePacingChange}
            onApplyPreset={handleApplyPreset}
            onSave={handleSave}
            saving={saving}
          />
        )
        : activeTab === 'synopsis' ? synopsisTab
        : <ArcTrackerTab pid={pid} onOpenContinuity={() => setShowContinuityDrawer(true)} />
      }
    </div>
  );

  if (!inDialog) return (
    <>
      <div className="p-4" data-testid="plot-builder-panel">{body}</div>
      <ContinuityReviewDrawer open={showContinuityDrawer} onClose={() => setShowContinuityDrawer(false)} project={project} />
    </>
  );

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => { if (!v && onClose) onClose(); }}>
        <DialogContent
          className="flex flex-col overflow-hidden p-0"
          style={{
            width: '75vw',
            height: '75vh',
            maxWidth: '75vw',
            maxHeight: '90vh',
            minWidth: '560px',
            minHeight: '480px',
            resize: 'both',
          }}
          data-testid="plot-builder-dialog"
        >
          <div className="flex-shrink-0 flex items-start justify-between gap-2 px-6 pt-5 pb-3 border-b border-border">
            <div>
              <DialogTitle className="flex items-center gap-2 text-[15px]">
                <GitBranch className="h-4 w-4 text-primary" /> Plot Builder
              </DialogTitle>
              <DialogDescription className="mt-0.5 text-[12px]">
                Extract and strengthen your manuscript's plot structure. Fields feed into Walker (Deep Plot Audit) and Drift Correction.
              </DialogDescription>
            </div>
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                data-testid="plot-builder-close-btn"
                className="mt-0.5 flex-shrink-0 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                aria-label="Close Plot Builder"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="flex-1 overflow-y-auto px-6 py-3 pr-5 space-y-3 scroll-thin">{body}</div>
        </DialogContent>
      </Dialog>
      <ContinuityReviewDrawer open={showContinuityDrawer} onClose={() => setShowContinuityDrawer(false)} project={project} />
    </>
  );
}

/** Thin wrapper that shows the panel inside a Dialog modal.
 *
 * IMPORTANT: We never return null when open=false — we always keep
 * PlotBuilderPanel in the tree and pass `open` down so Radix Dialog
 * can run its close animation and clean up aria-hidden / scroll-lock
 * properly.  Unmounting with open=true leaves the body locked.
 */
export default function PlotBuilderDialog({ open, onClose, project }) {
  return <PlotBuilderPanel project={project} inDialog open={open} onClose={onClose} />;
}
